<template>
  <WorkDataTable
    ref="workDataTable"
    v-bind="{ ...filteredProps, ...$attrs }"
    :items="lazyItems"
    :sortBy="sortBy"
    :sortDesc="sortDesc"
    :loading="isPending"
    :totalHits="totalHits"
    :maxTotalHits="maxTotalHits"
    :aggregations="aggregations"
    v-on="listeners"
    @click:row="clickRow($event)"
  >
    <template
      v-for="(_, name) in $scopedSlots"
      :slot="name"
      slot-scope="slotData"
    >
      <slot
        v-bind="slotData"
        :name="name"
      />
    </template>
  </WorkDataTable>
</template>

<script>
import WorkDataTable from './WorkDataTable';
import lazyLoading from '../../../mixins/lazy-loading-mixin';
import { filterProps } from '@/utils/functional-component-utils';
import fetchExcelDataMixin from '@/components/global_components/tables/mixins/fetch-excel-data-mixin';
import cloneDeep from 'lodash/cloneDeep';

export default {
  name: 'LazyLoadingTable',
  components: { WorkDataTable },
  mixins: [
    lazyLoading(
      (self) => self.path,
      (self) => ({
        ...self.filters,
        search: self.search,
        sort: self.sortBy,
        sortDesc: self.sortDesc,
        mandatoryFilters: self.mandatoryFilters,
      }),
      (self) => self.resultPageSize,
      (self) => self.getData,
      (self) => self.invokedParamHandler,
      (self) => self.isBaseLevelTableModule,
      (self) => self.customLoad,
      undefined,
      undefined,
      undefined,
      (self) => self.loadTotalHits,
    ),
    fetchExcelDataMixin(),
  ],
  props: {
    ...filterProps(WorkDataTable.props, [
      'totalHits',
      'aggregations',
      'maxTotalHits',
    ]),
    path: { type: String },
    search: { type: String, default: '' },
    filters: { type: Object, default: () => { } },
    mandatoryFilters: {
      type: Object,
      default: function () {
        return {};
      },
    },
    sortBy: { type: Array, default: () => [] },
    sortDesc: { type: Array, default: () => [] },
    getData: {
      type: Function,
      default: (response) => response.data?.data?.map((item) => item.data),
    },
    dirtyDialogIsOpen: { type: Boolean, default: false },
    customLoad: { type: Function, default: undefined },
    truncateData: { type: Boolean, required: false },
    invokedParamHandler: { type: Function },
    isClickable: { type: Boolean, default: true },
    isBaseLevelTableModule: { type: Boolean, default: false },
    title: { type: String },
    disableLazyLoading: { type: Boolean, default: false },
    resultPageSize: { type: Number, default: 50 },
    loadTotalHits: { type: Boolean, default: true },
    // the purpose of the two props below is to initially set the table into loading state so that 
    // data is not fetched yet and then manually trigger a reload when data is supposed to be fetched
    // use case for base level table: prevent data loading before the intitial filters (or preset filter) are finalized
    enterLoadingStateInitially: { type: Boolean, default: false }, // if true the table enters loading state on mount
    toggleForceReload: { type: Boolean, default: false }, // when this flag changes to true, the table will force a reload
  },
  data() {
    return {
      maxTotalHits: undefined,
      target: undefined,
    };
  },
  mounted() {
    this.target = this.$el.getElementsByClassName('v-data-table__wrapper')[0];
    this.target.addEventListener('scroll', this.handleScrollEvent, false);

    if (this.enterLoadingStateInitially && !this.toggleForceReload) {
      this.isLoading = true;
    }
    else
      this.reload();
    // Event (openFirstRow) is emitted by SearchBar.vue
    // this.$root.$on('openFirstRow', () => {
    //   const indexOfFirstItem = 0;
    //   const firstElementInTable = this.lazyItems[indexOfFirstItem];

    //   if (firstElementInTable != undefined) {
    //     this.$emit('click:row', this.lazyItems[indexOfFirstItem]);
    //   }
    // });
  },
  beforeDestroy() {
    this.cleanup();
  },
  deactivated() {
    this.cleanup();
  },
  computed: {
    filteredProps() {
      return filterProps(this.$props, ['search']);
    },
    listeners() {
      const listeners = cloneDeep(this.$listeners);
      delete listeners['click:row'];
      if (!this.isClickable) {
        delete listeners['input'];
      }
      return listeners;
    }
  },
  methods: {
    reloadTable() {
      this.$refs.workDataTable.clearSelection();
      this.reload();
    },
    handleScrollEvent() {
      let target = this.$el.getElementsByClassName('v-data-table__wrapper')[0];
      let height = target.scrollHeight - target.offsetHeight;
      let scrollTop = target.scrollTop;
      let distanceToBottom = height - scrollTop;

      if (distanceToBottom < 20 && !this.noNewResults && !this.disableLazyLoading) {
        this.loadNextData();
      }
    },
    clickRow(row) {
      if (this.isPending === true || !this.isClickable) {
        return;
      }
      this.$emit('click:row', row);
    },
    cleanup() {
      this.target?.removeEventListener('scroll', this.handleScrollEvent, false);
      this.target = undefined;
    },
  },
  watch: {
    lazyItems() {
      this.$emit('update:lazy-items', this.lazyItems);
    },
    toggleForceReload(newVal) {
      if (newVal === true) {
        this.isLoading = false;
        this.reloadTable();
      }
    }
  },
};
</script>
